// ./src/components/features/Logbook/LogbookSelectionSummary/LogbookSelectionSummary.tsx
//
// Unified selection summary bar for the Logbook page.
// Smart display: shows different stats based on what types are selected.
//
// Always shown:
//   - Selection count (with type breakdown badges when mixed)
//   - Total cost (when any refuels or expenses selected)
//
// When refuels are in selection:
//   - Total volume
//   - Price-per-unit min/max/avg (only when all records share the same currency)
//
// When expenses are in selection:
//   - Work + Parts breakdown (if any have breakdowns)
//
// When 2+ same-car records have odometer:
//   - Distance span
//   - Distance-between min/max/avg
//   NOTE: r.odometer is already in the car's display unit — no conversion needed.
//
// Layout: responsive mobile (stacked) vs desktop (horizontal).
// Mobile: sticky above the bottom tab bar via useStickyBottomOffset().

import React, { useMemo, useCallback } from "react";
import {
  Paper,
  Group,
  Text,
  Stack,
  Box,
  Badge,
  CloseButton,
  Divider,
  Tooltip,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { EXPENSE_TYPES } from "@/config";

import { Expense } from "@/utils/api/ExpenseService";
import { formatCurrency } from "@/utils/formatHelpers";

import { useUserProfileContext } from "@/contexts/UserProfileContext";
import { useStickyBottomOffset } from "@/hooks/useStickyBottomOffset";

import { AppIcon } from "@/components/generic/AppIcon";


// ============================================
// Types
// ============================================

export interface LogbookSelectionSummaryProps {
  /** Currently selected records */
  selectedRecords: Expense[];

  /** All records in the list (reserved for future contiguity checks) */
  allRecords: Expense[];

  /** Callback to clear selection */
  onClearSelection: () => void;

  /** Position: bottom = sticky at viewport bottom */
  position?: "bottom" | "inline";
}

// ============================================
// Internal stat types
// ============================================

interface MinMaxAvg {
  min: number;
  max: number;
  avg: number;
}

interface SummaryStats {
  // Counts
  totalCount: number;
  refuelCount: number;
  expenseCount: number;
  checkpointCount: number;

  // Cost (refuels + expenses only, summed in home currency)
  totalCost: number;
  currency: string | null;
  isSameCurrency: boolean;

  // Refuel-specific
  totalVolume: number;
  /** Only populated when isSameCurrency is true and refuels have volume+price */
  pricePerUnitStats: MinMaxAvg | null;

  // Expense-specific
  totalWork: number;
  totalParts: number;

  // Distance (same-car records with odometer)
  // r.odometer is already in the car's display unit — values used as-is.
  distanceSpan: number | null;
  distanceStats: MinMaxAvg | null;
  isSameCar: boolean;
}

// ============================================
// Type badge config
// ============================================

const TYPE_BADGE_CONFIG = [
  { type: EXPENSE_TYPES.REFUEL, color: "green", icon: "GasStation" as const },
  { type: EXPENSE_TYPES.EXPENSE, color: "blue", icon: "Receipt" as const },
  { type: EXPENSE_TYPES.CHECKPOINT, color: "orange", icon: "Flag" as const },
];

// ============================================
// Stats calculation (pure function)
// ============================================

const calculateStats = (selected: Expense[]): SummaryStats => {
  const empty: SummaryStats = {
    totalCount: 0,
    refuelCount: 0,
    expenseCount: 0,
    checkpointCount: 0,
    totalCost: 0,
    currency: null,
    isSameCurrency: true,
    totalVolume: 0,
    pricePerUnitStats: null,
    totalWork: 0,
    totalParts: 0,
    distanceSpan: null,
    distanceStats: null,
    isSameCar: false,
  };

  if (selected.length === 0) return empty;

  let totalCost = 0;
  let totalVolume = 0;
  let totalWork = 0;
  let totalParts = 0;
  let refuelCount = 0;
  let expenseCount = 0;
  let checkpointCount = 0;

  const paymentCurrencies = new Set<string>();  // used only for pricePerUnit gate
  let allCostRecordsHcResolvable = true;        // false if any cost record lacks HC value
  const pricesPerUnit: number[] = [];

  const carIds = new Set<string>();
  const odometerValues: number[] = [];

  for (const r of selected) {
    const type = r.expenseType ?? 0;

    // Count by type
    if (type === EXPENSE_TYPES.REFUEL) refuelCount++;
    else if (type === EXPENSE_TYPES.EXPENSE) expenseCount++;
    else if (type === EXPENSE_TYPES.CHECKPOINT) checkpointCount++;

    // Cost — only for refuels and expenses
    if (type !== EXPENSE_TYPES.CHECKPOINT) {
      const hasCostValue = r.totalPrice != null && r.totalPrice > 0;
      if (hasCostValue) {
        // A record is HC-resolvable when the user has entered the HC amount,
        // or when they paid in home currency (totalPriceInHc === totalPrice).
        const hcResolvable =
          (r.totalPriceInHc != null && r.totalPriceInHc > 0) ||
          r.paidInCurrency == null ||
          r.paidInCurrency.toUpperCase() === (r.homeCurrency ?? "").toUpperCase();

        if (!hcResolvable) allCostRecordsHcResolvable = false;

        totalCost += r.totalPriceInHc ?? r.totalPrice ?? 0;
      }
    }

    // Track payment currencies for pricePerUnit gate only
    if (r.paidInCurrency) paymentCurrencies.add(r.paidInCurrency.toUpperCase());

    // Refuel-specific
    if (type === EXPENSE_TYPES.REFUEL) {
      if (r.refuelVolume != null && r.refuelVolume > 0) {
        totalVolume += r.refuelVolume;
        // Collect price-per-unit; gated on same payment currency below
        if (r.totalPrice != null && r.totalPrice > 0) {
          pricesPerUnit.push(r.totalPrice / r.refuelVolume);
        }
      }
    }

    // Expense-specific
    if (type === EXPENSE_TYPES.EXPENSE) {
      totalWork += r.costWork || 0;
      totalParts += r.costParts || 0;
    }

    // Distance tracking — r.odometer already in car's display unit
    if (r.carId) carIds.add(r.carId);
    if (r.odometer != null) odometerValues.push(r.odometer);
  }

  // Currency for display.
  // When all cost records are HC-resolvable the sum is always in home currency,
  // so we surface homeCurrency as the display currency.
  const homeCurrencyValue =
    selected.find((r) => r.homeCurrency)?.homeCurrency ?? null;
  const currency = allCostRecordsHcResolvable
    ? homeCurrencyValue
    : selected.find((r) => r.paidInCurrency)?.paidInCurrency ?? null;
  const isSameCurrency = allCostRecordsHcResolvable;

  // Price per unit stats — only meaningful when all refuels share the same payment currency
  // (mixing EUR/L and CAD/L would produce a meaningless average).
  let pricePerUnitStats: MinMaxAvg | null = null;

  if (pricesPerUnit.length > 0 && paymentCurrencies.size <= 1) {
    pricePerUnitStats = {
      min: Math.round(Math.min(...pricesPerUnit) * 1000) / 1000,
      max: Math.round(Math.max(...pricesPerUnit) * 1000) / 1000,
      avg: Math.round(
        (pricesPerUnit.reduce((s, v) => s + v, 0) / pricesPerUnit.length) * 1000
      ) / 1000,
    };
  }

  // Distance stats — only when same car and 2+ odometer readings
  const isSameCar = carIds.size === 1;
  let distanceSpan: number | null = null;
  let distanceStats: MinMaxAvg | null = null;

  if (isSameCar && odometerValues.length >= 2) {
    odometerValues.sort((a, b) => a - b);
    distanceSpan = Math.round(odometerValues[odometerValues.length - 1] - odometerValues[0]);

    const gaps: number[] = [];
    for (let i = 1; i < odometerValues.length; i++) {
      const d = odometerValues[i] - odometerValues[i - 1];
      if (d > 0) gaps.push(d);
    }
    if (gaps.length > 0) {
      distanceStats = {
        min: Math.round(Math.min(...gaps)),
        max: Math.round(Math.max(...gaps)),
        avg: Math.round(gaps.reduce((s, v) => s + v, 0) / gaps.length),
      };
    }
  }

  return {
    totalCount: selected.length,
    refuelCount,
    expenseCount,
    checkpointCount,
    totalCost: Math.round(totalCost * 100) / 100,
    currency,
    isSameCurrency,
    totalVolume: Math.round(totalVolume * 100) / 100,
    pricePerUnitStats,
    totalWork: Math.round(totalWork * 100) / 100,
    totalParts: Math.round(totalParts * 100) / 100,
    distanceSpan,
    distanceStats,
    isSameCar,
  };
};

// ============================================
// Sub-components
// ============================================

interface StatItemProps {
  label: string;
  value: string;
  icon?: string;
  bold?: boolean;
}

const StatItem: React.FC<StatItemProps> = ({ label, value, icon, bold = false }) => (
  <Group gap={4} wrap="nowrap">
    {icon && <AppIcon iconName={icon} size={16} color="dimmed" />}
    <Text size="md" c="dimmed">{label}:</Text>
    <Text size="md" fw={bold ? 600 : 500}>{value}</Text>
  </Group>
);

interface MinMaxAvgRowProps {
  label: string;
  stats: MinMaxAvg;
  unit?: string;
  formatValue?: (v: number) => string;
}

const MinMaxAvgRow: React.FC<MinMaxAvgRowProps> = ({
  label,
  stats,
  unit = "",
  formatValue = (v) => v.toLocaleString(),
}) => {
  const { t } = useTranslation(["logbook"]);
  const u = unit ? ` ${unit}` : "";

  return (
    <Group gap="xs" wrap="nowrap">
      <Text size="sm" c="dimmed" fw={500}>{label}:</Text>
      <Text size="sm" c="dimmed">
        {t("logbook:selection.statsMin", { defaultValue: "min" })}{" "}
        {formatValue(stats.min)}{u}
      </Text>
      <Text size="sm" c="dimmed">/</Text>
      <Text size="sm" c="dimmed">
        {t("logbook:selection.statsMax", { defaultValue: "max" })}{" "}
        {formatValue(stats.max)}{u}
      </Text>
      <Text size="sm" c="dimmed">/</Text>
      <Text size="sm" fw={500}>
        {t("logbook:selection.statsAvg", { defaultValue: "avg" })}{" "}
        {formatValue(stats.avg)}{u}
      </Text>
    </Group>
  );
};

// ============================================
// Main Component
// ============================================

const LogbookSelectionSummaryComponent: React.FC<LogbookSelectionSummaryProps> = ({
  selectedRecords,
  allRecords: _allRecords,
  onClearSelection,
  position = "bottom",
}) => {
  const { t } = useTranslation(["logbook", "common"]);
  const {
    currency: homeCurrency,
    volumeLabelShort,
    distanceLabelShort,
  } = useUserProfileContext();

  const stickyBottom = useStickyBottomOffset();

  // ── Stats ──

  const stats = useMemo(
    () => calculateStats(selectedRecords),
    [selectedRecords]
  );

  // ── Formatters — using shared formatHelpers ──

  const fmtAmount = useCallback(
    (amount: number, currency: string | null) =>
      formatCurrency(amount, currency || homeCurrency),
    [homeCurrency]
  );

  const fmtPricePerUnit = useCallback(
    (value: number) =>
      formatCurrency(value, stats.currency || homeCurrency, undefined, {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      }),
    [homeCurrency, stats.currency]
  );

  // ── Visibility flags ──

  const hasCost = stats.totalCost > 0;
  const hasVolume = stats.totalVolume > 0;
  const hasWorkParts = stats.totalWork > 0 || stats.totalParts > 0;
  const hasDistance = stats.distanceSpan != null && stats.distanceSpan > 0 && stats.isSameCar;
  const hasDetailStats = stats.pricePerUnitStats != null || stats.distanceStats != null;

  const activeTypeCount = [stats.refuelCount, stats.expenseCount, stats.checkpointCount]
    .filter((c) => c > 0).length;
  const hasMultipleTypes = activeTypeCount > 1;

  // ── Type badges ──

  const typeBadges = useMemo(() => {
    return TYPE_BADGE_CONFIG.filter(({ type }) => {
      if (type === EXPENSE_TYPES.REFUEL) return stats.refuelCount > 0;
      if (type === EXPENSE_TYPES.EXPENSE) return stats.expenseCount > 0;
      if (type === EXPENSE_TYPES.CHECKPOINT) return stats.checkpointCount > 0;
      return false;
    }).map(({ type, color, icon }) => {
      const count =
        type === EXPENSE_TYPES.REFUEL ? stats.refuelCount :
          type === EXPENSE_TYPES.EXPENSE ? stats.expenseCount :
            stats.checkpointCount;
      return (
        <Badge
          key={type}
          variant="light"
          color={color}
          size="md"
          leftSection={<AppIcon iconName={icon} size={12} />}
        >
          {count}
        </Badge>
      );
    });
  }, [stats.refuelCount, stats.expenseCount, stats.checkpointCount]);

  // ── Don't render when nothing selected ──

  if (stats.totalCount === 0) return null;

  // ── Position styles ──

  const positionStyles: React.CSSProperties =
    position === "bottom"
      ? { position: "sticky", bottom: stickyBottom, zIndex: 100, marginBottom: "var(--mantine-spacing-md)" }
      : { position: "sticky", top: 0, zIndex: 100 };

  // ============================================
  // Mobile Layout
  // ============================================

  const mobileLayout = (
    <Box hiddenFrom="sm">
      <Stack gap="xs">
        {/* Row 1: count + type badges + close */}
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs" wrap="nowrap">
            <AppIcon iconName="CircleCheck" size={20} color="blue" />
            <Text size="md" fw={700}>
              {t("logbook:selection.count", {
                count: stats.totalCount,
                defaultValue: "{{count}} selected",
              })}
            </Text>
            {hasMultipleTypes && <Group gap={4}>{typeBadges}</Group>}
          </Group>
          <CloseButton
            size="md"
            onClick={onClearSelection}
            aria-label={t("common:buttons.clearSelection", { defaultValue: "Clear selection" })}
          />
        </Group>

        {/* Row 2: primary stats */}
        <Group gap="md" wrap="wrap">
          {hasCost && (
            <Group gap={4} wrap="nowrap">
              <AppIcon iconName="CurrencyDollar" size={16} color="dimmed" />
              <Text size="md" fw={600}>
                {stats.isSameCurrency
                  ? fmtAmount(stats.totalCost, stats.currency)
                  : t("logbook:selection.mixedCurrencies", { defaultValue: "Mixed currencies" })}
              </Text>
            </Group>
          )}

          {hasVolume && (
            <Group gap={4} wrap="nowrap">
              <AppIcon iconName="GasStation" size={16} color="dimmed" />
              <Text size="md" fw={500}>
                {stats.totalVolume} {t(`common:${volumeLabelShort}`)}
              </Text>
            </Group>
          )}

          {hasDistance && (
            <Group gap={4} wrap="nowrap">
              <AppIcon iconName="Route" size={16} color="dimmed" />
              <Text size="md" fw={500}>
                {stats.distanceSpan!.toLocaleString()} {t(`common:${distanceLabelShort}`)}
              </Text>
            </Group>
          )}

          {stats.totalWork > 0 && (
            <Group gap={4} wrap="nowrap">
              <AppIcon iconName="Tool" size={16} color="dimmed" />
              <Text size="md" fw={500}>{fmtAmount(stats.totalWork, stats.currency)}</Text>
            </Group>
          )}

          {stats.totalParts > 0 && (
            <Group gap={4} wrap="nowrap">
              <AppIcon iconName="Package" size={16} color="dimmed" />
              <Text size="md" fw={500}>{fmtAmount(stats.totalParts, stats.currency)}</Text>
            </Group>
          )}
        </Group>

        {/* Row 3: detail stats */}
        {hasDetailStats && (
          <>
            <Divider />
            <Stack gap={4}>
              {stats.pricePerUnitStats && (
                <Group gap="xs" wrap="wrap">
                  <Text size="sm" c="dimmed" fw={500}>
                    {t("logbook:selection.pricePerUnit", { defaultValue: "Price/unit" })}:
                  </Text>
                  <Text size="sm">
                    {fmtPricePerUnit(stats.pricePerUnitStats.min)} –{" "}
                    {fmtPricePerUnit(stats.pricePerUnitStats.max)}
                  </Text>
                  <Text size="sm" fw={500}>
                    ({t("logbook:selection.statsAvg", { defaultValue: "avg" })}{" "}
                    {fmtPricePerUnit(stats.pricePerUnitStats.avg)})
                  </Text>
                </Group>
              )}
              {stats.distanceStats && (
                <Group gap="xs" wrap="wrap">
                  <Text size="sm" c="dimmed" fw={500}>
                    {t("logbook:selection.distanceBetween", { defaultValue: "Between records" })}:
                  </Text>
                  <Text size="sm">
                    {stats.distanceStats.min.toLocaleString()} –{" "}
                    {stats.distanceStats.max.toLocaleString()} {t(`common:${distanceLabelShort}`)}
                  </Text>
                  <Text size="sm" fw={500}>
                    ({t("logbook:selection.statsAvg", { defaultValue: "avg" })}{" "}
                    {stats.distanceStats.avg.toLocaleString()} {t(`common:${distanceLabelShort}`)})
                  </Text>
                </Group>
              )}
            </Stack>
          </>
        )}

        {!stats.isSameCar && stats.totalCount >= 2 && (
          <Text size="sm" c="dimmed" fs="italic">
            {t("logbook:selection.multipleVehicles", {
              defaultValue: "Records from multiple vehicles — distance stats unavailable",
            })}
          </Text>
        )}
      </Stack>
    </Box>
  );

  // ============================================
  // Desktop Layout
  // ============================================

  const desktopLayout = (
    <Box visibleFrom="sm">
      <Stack gap="xs">
        {/* Primary row */}
        <Group justify="space-between" wrap="nowrap">
          <Group gap="lg" wrap="nowrap">
            <Group gap="xs" wrap="nowrap">
              <AppIcon iconName="CircleCheck" size={20} color="blue" />
              <Text size="md" fw={700}>
                {t("logbook:selection.count", {
                  count: stats.totalCount,
                  defaultValue: "{{count}} selected",
                })}
              </Text>
              {hasMultipleTypes && <Group gap={4} ml={4}>{typeBadges}</Group>}
            </Group>

            {hasCost && (
              <>
                <Text c="dimmed">•</Text>
                <StatItem
                  label={t("logbook:selection.total", { defaultValue: "Total" })}
                  value={
                    stats.isSameCurrency
                      ? fmtAmount(stats.totalCost, stats.currency)
                      : t("logbook:selection.mixedCurrencies", { defaultValue: "Mixed currencies" })
                  }
                  bold
                />
              </>
            )}

            {hasVolume && (
              <>
                <Text c="dimmed">•</Text>
                <StatItem
                  label={t("logbook:selection.volume", { defaultValue: "Volume" })}
                  value={`${stats.totalVolume} ${t(`common:${volumeLabelShort}`)}`}
                />
              </>
            )}

            {hasWorkParts && (
              <>
                <Text c="dimmed">•</Text>
                {stats.totalWork > 0 && (
                  <StatItem
                    label={t("logbook:selection.work", { defaultValue: "Work" })}
                    value={fmtAmount(stats.totalWork, stats.currency)}
                  />
                )}
                {stats.totalWork > 0 && stats.totalParts > 0 && <Text c="dimmed">+</Text>}
                {stats.totalParts > 0 && (
                  <StatItem
                    label={t("logbook:selection.parts", { defaultValue: "Parts" })}
                    value={fmtAmount(stats.totalParts, stats.currency)}
                  />
                )}
              </>
            )}

            {hasDistance && (
              <>
                <Text c="dimmed">•</Text>
                <Tooltip
                  label={t("logbook:selection.distanceTooltip", {
                    defaultValue: "Distance between earliest and latest odometer readings in selection",
                  })}
                  withArrow
                >
                  <Group gap={4} wrap="nowrap" style={{ cursor: "default" }}>
                    <AppIcon iconName="Route" size={16} color="dimmed" />
                    <Text size="md" fw={500}>
                      {stats.distanceSpan!.toLocaleString()} {t(`common:${distanceLabelShort}`)}
                    </Text>
                  </Group>
                </Tooltip>
              </>
            )}
          </Group>

          <CloseButton
            size="md"
            onClick={onClearSelection}
            aria-label={t("common:buttons.clearSelection", { defaultValue: "Clear selection" })}
          />
        </Group>

        {/* Detail stats row */}
        {hasDetailStats && (
          <Group gap="lg" wrap="wrap">
            {stats.pricePerUnitStats && (
              <MinMaxAvgRow
                label={t("logbook:selection.pricePerUnit", { defaultValue: "Price/unit" })}
                stats={stats.pricePerUnitStats}
                unit={`/${t(`common:${volumeLabelShort}`)}`}
                formatValue={fmtPricePerUnit}
              />
            )}
            {stats.distanceStats && (
              <MinMaxAvgRow
                label={t("logbook:selection.distanceBetween", { defaultValue: "Between records" })}
                stats={stats.distanceStats}
                unit={t(`common:${distanceLabelShort}`)}
              />
            )}
          </Group>
        )}

        {!stats.isSameCar && stats.totalCount >= 2 && (
          <Text size="sm" c="dimmed" fs="italic">
            {t("logbook:selection.multipleVehicles", {
              defaultValue: "Records from multiple vehicles — distance stats unavailable",
            })}
          </Text>
        )}
      </Stack>
    </Box>
  );

  // ============================================
  // Render
  // ============================================

  return (
    <Paper
      shadow="md"
      p="md"
      withBorder
      radius="md"
      style={positionStyles}
      bg="var(--mantine-color-body)"
    >
      {mobileLayout}
      {desktopLayout}
    </Paper>
  );
};

export const LogbookSelectionSummary = React.memo(LogbookSelectionSummaryComponent);